// BOORO: the organigram over every office of the person. An office (Space) is
// the boundary of the data; groups inside it only order.
import { oc } from "@orpc/contract";
import * as z from "zod";
import { TeamAgentSchema } from "./booro-team.js";
import { GROUP_NAME_MAX_LENGTH } from "./domain.js";
import { Id } from "./ids.js";

/** An office: agents in it share computers, access and memory. */
export const OfficeSchema = z.object({
  id: Id,
  name: z.string(),
  /** Where the person put the office on the canvas; null lets the canvas place it. */
  x: z.number().int().nullable().optional(),
  y: z.number().int().nullable().optional(),
  color: z.string().nullable().optional(),
  /** The agent that leads the office, under the Chief. */
  leadBotId: Id.nullable().optional(),
});
export type Office = z.infer<typeof OfficeSchema>;

/**
 * The colours a group or an office can carry, so they are told apart at a
 * glance: the Boo Crew colours of the ghosts, in the same order.
 */
export const GROUP_COLORS = [
  "#FFFFFF",
  "#875DEF",
  "#62B785",
  "#E07932",
  "#64B4D1",
  "#5D80EF",
  "#DCB53B",
  "#866F64",
  "#D44F4A",
  "#D25297",
  "#697489",
] as const;
export const GroupColorSchema = z.enum(GROUP_COLORS);

export const OrgGroupSchema = z.object({
  id: Id,
  spaceId: Id,
  parentGroupId: Id.nullable(),
  name: z.string(),
  /** The agent that leads the group. */
  leadBotId: Id.nullable(),
  color: z.string().nullable(),
  /** Where the person put the group inside its parent; null lets the canvas place it. */
  x: z.number().int().nullable(),
  y: z.number().int().nullable(),
  position: z.number().int(),
});
export type OrgGroup = z.infer<typeof OrgGroupSchema>;

/** An agent with the office and the group it sits in. */
export const OrgAgentSchema = TeamAgentSchema.extend({
  spaceId: Id,
  /** Null when the agent sits directly in the office. */
  groupId: Id.nullable(),
  position: z.number().int(),
});
export type OrgAgent = z.infer<typeof OrgAgentSchema>;

export const OrgSnapshotSchema = z.object({
  /** The account all offices belong to. */
  organizationId: Id,
  /** The office the app has open; detail panels work in it. */
  currentSpaceId: Id,
  /** The Chief of the account: above every office and group. */
  chiefBotId: Id.nullable(),
  offices: z.array(OfficeSchema),
  groups: z.array(OrgGroupSchema),
  agents: z.array(OrgAgentSchema),
});
export type OrgSnapshot = z.infer<typeof OrgSnapshotSchema>;

const GroupName = z.string().trim().min(1).max(GROUP_NAME_MAX_LENGTH);
const Ok = z.object({ ok: z.literal(true) });

export const booroOrgContract = {
  /** Every office of the person, with its groups and agents. */
  snapshot: oc.output(OrgSnapshotSchema),
  createGroup: oc
    .input(
      z.object({
        spaceId: Id,
        parentGroupId: Id.nullable().optional(),
        name: GroupName,
        color: GroupColorSchema.nullable().optional(),
      }),
    )
    .output(OrgGroupSchema),
  updateGroup: oc
    .input(
      z.object({
        groupId: Id,
        name: GroupName.optional(),
        leadBotId: Id.nullable().optional(),
        color: GroupColorSchema.nullable().optional(),
      }),
    )
    .output(Ok),
  /**
   * Puts a group under another one in the same office, or at the top of its
   * office, at the place the person dropped it.
   */
  moveGroup: oc
    .input(
      z.object({
        groupId: Id,
        parentGroupId: Id.nullable(),
        x: z.number().int().min(0).max(100_000).optional(),
        y: z.number().int().min(0).max(100_000).optional(),
      }),
    )
    .output(Ok),
  /** Puts an office where the person dropped it on the canvas. */
  placeOffice: oc
    .input(
      z.object({
        spaceId: Id,
        x: z.number().int().min(-100_000).max(100_000),
        y: z.number().int().min(-100_000).max(100_000),
      }),
    )
    .output(Ok),
  /** Gives an office one of the group colours, or none. */
  colorOffice: oc.input(z.object({ spaceId: Id, color: GroupColorSchema.nullable() })).output(Ok),
  /** Makes an agent of the office its lead, or leaves the office without one. */
  leadOffice: oc.input(z.object({ spaceId: Id, leadBotId: Id.nullable() })).output(Ok),
  /** Removes a group; what was in it moves one level up. */
  removeGroup: oc.input(z.object({ groupId: Id })).output(Ok),
  /** Puts an agent into a group of its office, or directly into the office. */
  placeAgent: oc.input(z.object({ botId: Id, groupId: Id.nullable() })).output(Ok),
};
